import { UserDoc } from '@gooduncles/gu-app-schema';
import { message } from 'antd';
import { User, UserCredential } from 'firebase/auth';
import { FC, PropsWithChildren, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { AuditLogger } from 'src/utils/audit-logger';
import { errorObjectToString, getPublicIp, refreshSessionIfNeeded } from 'src/utils/common';
import { requestFcmTokenAndRegister, unregisterFcmToken } from 'src/utils/firebase/firebase-messaging';
import {
  fbCreateUserWithEmailAndPassword,
  fbGetCurrentUser,
  fbObserveAuthState,
  fbSignInWithEmailAndPassword,
  fbSignOut,
  observePartnersUsers,
  observeUser,
  updateUser,
} from 'src/utils/firebase/firebase-short-cut';
import { ConsoleLogger } from 'src/utils/logger';

import usePartners from 'src/redux/hooks/usePartners';

const logger = ConsoleLogger.getInstance();
const auditLogger = AuditLogger.getInstance();

type AuthUser = {
  uid: string;
  email: string | null;
  name: string | null;
  provider: string;
  photoUrl: string | null;
  stripeRole: string | object;
  token: string;
};

interface AuthContext {
  user?: UserDoc;
  authUser: false | AuthUser | null;
  authLoading: boolean;
  isLoggedIn: boolean;
  createUserWithEmailAndPassword: (email: string, password: string) => Promise<UserCredential>;
  signinWithEmail: (email: string, password: string) => Promise<UserCredential>;
  signout: () => Promise<void>;
  setAuthLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const authContext = createContext<AuthContext | null>(null);

export const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(authContext);
  if (context == null) {
    throw new Error('Out of context: authProvider is not set');
  }
  return context;
};

const useProvideAuth = () => {
  const navigate = useNavigate();
  // 1. 최초 진입시 유저 정보는 null이다.
  const [authUser, setAuthUser] = useState<AuthUser | null | false>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [user, setUser] = useState<UserDoc>();
  const isLoggedIn = useMemo(() => !!user?._id, [user?._id]);
  const authUserId = useMemo(() => authUser && authUser.uid, [authUser]);
  const { onSetPartnersUsers } = usePartners();

  const createUserWithEmailAndPassword = (email: string, password: string) => {
    setAuthLoading(true);
    return fbCreateUserWithEmailAndPassword(email, password);
  };

  const signinWithEmail = (email: string, password: string) => {
    setAuthLoading(true);
    return fbSignInWithEmailAndPassword(email, password);
  };

  const signout = useCallback(async () => {
    navigate('/');
    // fbSignOut 이후엔 Firestore write 권한이 없으므로 반드시 sign-out 전에 정리.
    await unregisterFcmToken();
    await fbSignOut();
  }, [navigate]);

  /**
   * 로그인 상태 변화에 따라 유저 정보에 변동이 있으면
   * 해당 내용에 따른 authUser의 상태를 업데이트한다.
   */
  const handleUser = useCallback(async (authUser: User | null) => {
    try {
      setAuthLoading(true);
      getPublicIp().then((ip) => {
        logger.setPublicIp(ip);
      });

      if (authUser) {
        const user = await formatUser(authUser);
        const { email, uid } = user;
        if (email === null) {
          throw new Error('email is null');
        }

        auditLogger.setUid(uid);
        const currentSessionId = refreshSessionIfNeeded();
        auditLogger.log({
          action: 'CREATE',
          entity: 'session',
          entityId: currentSessionId,
          summary: `세션 시작: ${email}`,
        });

        // 1. firebase의 가장 기본 user 정보를 업데이트한다.
        // 사용자의 auth정보에서 이메일이 변경된 경우에 해당할 수 있다.
        const userDoc: Partial<UserDoc> = {
          email,
          ip: logger.getPublicIp(),
          sessionId: currentSessionId,
        };
        setAuthUser(user);

        await updateUser(uid, userDoc);
        setAuthLoading(false);
        return user;
      }
    } catch (error) {
      console.error(error);
      const description = errorObjectToString(error);
      toast.error(description);
    }

    // 위에서 return에 실패한 경우
    logger.setLoggerUser(null);
    auditLogger.setUid(null);
    setAuthUser(false);
    setAuthLoading(false);
    return false;
  }, []);

  // 최초 앱 실행후 auth를 Provide하는 순간부터
  // 하위 컴포넌트를 기준으로
  // 로그인 상태변화는 계속 관찰상태에 놓인다.
  useEffect(() => {
    const subscription = fbObserveAuthState().subscribe(handleUser);
    return () => subscription.unsubscribe();
  }, [handleUser]);

  useEffect(() => {
    if (authUserId) {
      const subscription = observeUser(authUserId).subscribe(async (user0) => {
        if (!user0) {
          return;
        }
        const AUTHORIZED_ROLES = ['manager', 'admin'];
        const isAuthorized = AUTHORIZED_ROLES.includes(user0.role);
        if (!isAuthorized) {
          message.error('사용권한이 없습니다. 로그아웃 합니다.');
          logger.setLoggerUser(user0);
          await auditLogger.log({
            action: 'STATUS_CHANGE',
            entity: 'session',
            entityId: authUserId,
            summary: `권한 부족으로 강제 로그아웃 - role: ${user0.role}, email: ${user0.email}`,
            level: 'error',
            metadata: { role: user0.role, email: user0.email },
          });
          await fbSignOut();
          return;
        }
        setUser(user0);
        logger.setLoggerUser(user0);
      });

      return () => {
        setUser(undefined);
        subscription.unsubscribe();
      };
    } else {
      setUser(undefined);
    }
  }, [authUserId]);

  useEffect(() => {
    if (isLoggedIn) {
      const subscription = observePartnersUsers().subscribe(onSetPartnersUsers);
      return () => subscription.unsubscribe();
    }
  }, [isLoggedIn, onSetPartnersUsers]);

  // admin 로그인 시 (브라우저 지원 + 권한 'granted' 한정) FCM 토큰을 발급/upsert.
  // 'default'/'denied'는 내부에서 silent skip되므로 PermissionBanner 동의 후 별도 호출 필요.
  useEffect(() => {
    if (user?.role !== 'admin' || !user?._id) return;
    requestFcmTokenAndRegister(user._id).catch((error) => {
      console.warn('[auth-context] FCM token register failed', error);
    });
  }, [user?.role, user?._id]);

  return {
    user,
    authUser,
    authLoading,
    isLoggedIn,
    createUserWithEmailAndPassword,
    signinWithEmail,
    signout,
    setAuthLoading,
  };
};

const getStripeRole = async () => {
  await fbGetCurrentUser()?.getIdToken(true);
  const decodedToken = await fbGetCurrentUser()?.getIdTokenResult();

  return decodedToken?.claims.stripeRole || 'free';
};

const formatUser = async (user: User): Promise<AuthUser> => {
  const token = await user.getIdToken();
  return {
    uid: user.uid,
    email: user.email,
    name: user.displayName,
    provider: user.providerData[0].providerId,
    photoUrl: user.photoURL,
    stripeRole: await getStripeRole(),
    token,
  };
};
